import { z } from "zod";
import { utcDatetime } from "../../../helpers/date";
import { AgentAccessGrantTargetSchema } from "../agentAccess/agent-access-grant.schema";

/**
 * v1 PAT scope vocabulary. The backend stores the permissions as `text[]` so
 * new values like `admin` / `billing:read` can be added without migrations —
 * the enum here is the pinned set we expose in the UI + CLI today.
 */
export const PatPermissionSchema = z.enum(["read", "write"]);
export type PatPermission = z.infer<typeof PatPermissionSchema>;

export const PAT_PERMISSIONS_DEFAULT: PatPermission[] = ["read", "write"];

export const PersonalAccessTokenSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    prefix: z.string(),
    workspaceId: z.string().uuid().nullable(),
    grantTarget: AgentAccessGrantTargetSchema.nullable(),
    permissions: z.array(PatPermissionSchema),
    lastUsedAt: utcDatetime.nullable(),
    expiresAt: utcDatetime.nullable(),
    revokedAt: utcDatetime.nullable(),
    createdAt: utcDatetime,
});

export type PersonalAccessToken = z.infer<typeof PersonalAccessTokenSchema>;

export const CreatePersonalAccessTokenSchema = z.object({
    name: z.string().trim().min(1).max(120),
    grantTarget: AgentAccessGrantTargetSchema.optional(),
    workspaceId: z.string().uuid().nullable().optional(),
    expiresAt: z.coerce.date().nullable().optional(),
    /** At least one scope must be present. */
    permissions: z.array(PatPermissionSchema).min(1).optional(),
});

export type CreatePersonalAccessTokenInput = z.infer<
    typeof CreatePersonalAccessTokenSchema
>;

export const CreatePersonalAccessTokenResponseSchema = z.object({
    token: PersonalAccessTokenSchema,
    /** The plaintext PAT. Only returned once at creation time. */
    plaintext: z.string(),
});

export type CreatePersonalAccessTokenResponse = z.infer<
    typeof CreatePersonalAccessTokenResponseSchema
>;

export const ListPersonalAccessTokensResponseSchema = z.object({
    tokens: z.array(PersonalAccessTokenSchema),
});

export const PatIdPathParamsSchema = z.object({
    id: z.string().uuid(),
});
